import React from 'react';
import { useHistory } from 'react-router-dom';
import useScrollAnimation from '../hooks/useScrollAnimation';
import '../styles/pig-farm.css';

const PigFarmService = () => {
  const history = useHistory();
  useScrollAnimation();

  const productData = {
    title: '스마트 돼지 농장 관리 시스템',
    description:
      'AI 기반 돼지 건강 모니터링 및 관리 시스템으로, 실시간 건강 상태 추적과 이상 징후 감지를 통해 농장의 생산성을 극대화합니다.',
    image: '/images/pig-farm.jpg',
    details: {
      overview:
        '투자자와 농장주가 접근하기 쉽고, 인터렉티브한 요소를 추가하여 재미를 주고, 원스톱 스토어로 수익률을 높일 수 있는 O2O플랫폼을 목표로 합니다.',
      appDescriptions: {
        funding:
          '펀딩: 농장주와 투자자를 연결하는 편리한 펀딩 플랫폼으로, 개인구좌 연동과 투명한 투자 관리 기능을 제공합니다.\n스토어: 돼지고기와 근교농산물을 원스톱으로 구매할 수 있는 스토어로, 신선한 제품을 합리적인 가격에 제공합니다.',
        store: 'unity 앱을 통해 가상의 환경에서 각 농장의 상태 확인이 가능합니다.',
        chat: '투자자와 농장주 간의 실시간 소통이 가능한 채팅 시스템으로, 농장 상황과 투자 현황을 즉시 공유할 수 있습니다.',
      },
      images: {
        monitoring: '/images/monitoring-system.jpg',
        unity: '/images/unity-3d.jpg',
        mobile: '/images/pig-farm.mp4',
        store: '/images/unity.mp4',
        farm: '/images/smart-farm.jpg',
        vision: '/images/vision-system.jpg',
        chat: '/images/chat-system.jpg',
        game: '/images/farm-game.jpg',
      },
      features: [
        {
          title: '펀딩 시스템',
          items: ['농장주의 접근이 편리한 개인구좌 연계 펀딩 앱', '돼지 구매 지분 투자 방식으로 수익률을 높임'],
        },
        {
          title: '스토어 연동',
          items: ['돼지고기 및 근처 농산물 판매', '일일 유통 매출 관리'],
        },
        {
          title: '메타버스 농장',
          items: [
            '돼지 농장 표준 메타버스 공간',
            '실제 농장과 가상 농장 연동',
            '농장 상태 및 열 분포 모니터링',
            '농장 시스템 제어',
          ],
        },
        {
          title: '돼지 관리',
          items: ['개별 돼지 생육기간 및 상태 확인', '돼지 모니터링 및 분석', '사육 및 판매 프로세스'],
        },
        {
          title: '인터렉티브 기능',
          items: ['투자 농장 가상 방문', '반응형 스피커로 음성/음악 전달', '돼지 그룹 먹이 판매'],
        },
        {
          title: '농장 상태 실시간 모니터링',
          items: ['모바일, PC에서 농장 상태 확인', '일별/주별 매출 추적'],
        },
      ],
    },
  };

  return (
    <section className="pig-farm-section">
      <div className="pig-farm-container">
        <h3 className="pig-farm-modal-title">{productData.title}</h3>

        <div className="pig-farm-image-grid">
          <div className="pig-farm-grid-image">
            <h5 className="pig-farm-image-title">스마트 돼지 농장</h5>
            <div className="pig-farm-image-container">
              <img src={productData.details.images.farm} alt="스마트 돼지 농장" />
            </div>
            <p className="pig-farm-image-description">AI 기반 돼지 건강 모니터링 및 관리 시스템</p>
          </div>
          <div className="pig-farm-grid-image">
            <h5 className="pig-farm-image-title">시스템 정보</h5>
            <div className="pig-farm-image-container">
              <img src="/images/pig-farm-info.gif" alt="시스템 정보" />
            </div>
            <p className="pig-farm-image-description">실시간 모니터링 및 데이터 분석 시스템</p>
          </div>
        </div>

        <div className="pig-farm-modal-section">
          <h4 className="pig-farm-modal-section-title">개요</h4>
          <p className="pig-farm-modal-description">{productData.details.overview}</p>
        </div>

        <div className="pig-farm-image-grid">
          <div className="pig-farm-grid-image">
            <h5 className="pig-farm-image-title">피그팜 앱</h5>
            <div className="pig-farm-image-container">
              <video
                src={productData.details.images.mobile}
                controls
                autoPlay
                muted
                loop
                style={{ width: '100%', height: '100%' }}
                alt="피그팜 앱"
              />
            </div>
            <p className="pig-farm-image-description">
              {productData.details.appDescriptions.funding.split('\n').map((line, index) => {
                const [title, ...rest] = line.split(':');
                return (
                  <React.Fragment key={index}>
                    <strong>{title}</strong> <br />
                    {rest.join(':')}
                    <br />
                  </React.Fragment>
                );
              })}
            </p>
          </div>
          <div className="pig-farm-grid-image">
            <h5 className="pig-farm-image-title">유니티 앱</h5>
            <div className="pig-farm-image-container">
              <video
                src={productData.details.images.store}
                controls
                autoPlay
                muted
                loop
                style={{ width: '100%', height: '100%' }}
                alt="스토어 앱"
              />
            </div>
            <p className="pig-farm-image-description">{productData.details.appDescriptions.store}</p>
          </div>
          <div className="pig-farm-grid-image">
            <h5 className="pig-farm-image-title">채팅 시스템</h5>
            <div className="pig-farm-image-container">
              <img src={productData.details.images.chat} alt="채팅 시스템" />
            </div>
            <p className="pig-farm-image-description">{productData.details.appDescriptions.chat}</p>
          </div>
        </div>

        <div className="pig-farm-modal-section">
          <h4 className="pig-farm-modal-section-title">주요 기능</h4>
          <div className="pig-farm-feature-grid">
            {productData.details.features.map((feature, index) => (
              <div key={index} className="pig-farm-feature-card">
                <h5>{feature.title}</h5>
                <ul>
                  {feature.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PigFarmService;
